import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  ErrorMessage,
  HighlightMatch,
  Label,
  Layout,
  Select,
  useSelect,
  type SelectOptionProps,
} from '@loadsmart/loadsmart-ui'
// TODO: Make Miranda export this type
import { type SelectDatasource } from '@loadsmart/loadsmart-ui/dist/components/Select/Select.types'

import { RequestStatus } from 'core/domain/Request'
import SelectDriverImage from 'core/ui/assets/select-driver.svg'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { Icon } from 'core/ui/components/icon/Icon'

import { useAnalytics } from 'legacy/containers/LoadsPage/hooks/useAnalytics'

import { type Driver } from '../../../domain/Driver'
import { type Shipment } from '../../../domain/shipment/Shipment'
import { useAssignDriver } from '../../hooks/useAssignDriver'
import { useCreateDriver } from '../../hooks/useCreateDriver'
import { useDrivers } from '../../hooks/useDrivers'
import { InputGroup, Input } from './DriverStep.styles'
import { ContentWrapper, ImageWrapper, Wrapper } from './Step.styles'
import { type StepProps } from './Step.types'

const ADD_NEW_DRIVER_OPTION: Driver = {
  id: 'add-new-driver-option',
  name: 'Add new driver',
  phoneNumber: '',
}

function hasError(value?: Array<string>): boolean {
  return Array.isArray(value) && value.length > 0
}

export function DriverStep({ loadOffer, shipment, onComplete, onMarkAsComplete }: StepProps) {
  const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>()
  const [name, setName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const { drivers, driversStatus } = useDrivers()
  const { assignDriver, assignDriverStatus } = useAssignDriver()
  const { createDriver, createDriverStatus, createDriverErrors } = useCreateDriver()
  const { analytics } = useAnalytics()
  const [driverOptions, setDriverOptions] = useState<Array<Driver>>([])

  const isAddingNewDriver = selectedDriver?.id === ADD_NEW_DRIVER_OPTION.id
  const hasNameError = hasError(createDriverErrors?.errors?.name)
  const hasPhoneNumberError = hasError(createDriverErrors?.errors?.phone_number)
  const isAssignDriverLoading = assignDriverStatus === RequestStatus.Pending
  const isCreateDriverLoading = createDriverStatus === RequestStatus.Pending
  const isAssignDriverInvalid = !isAddingNewDriver && !selectedDriver
  const isCreateDriverInvalid = isAddingNewDriver && (!name || !phoneNumber)

  async function handleFinish() {
    let driver = !isAddingNewDriver ? selectedDriver : undefined

    if (isAddingNewDriver) {
      try {
        driver = await createDriver({ name: name.trim(), phoneNumber: phoneNumber.trim() })

        analytics.track(['Load Setup', 'Driver Assign Step', 'Create Driver'])
      } catch {
        toast.error('Failed to create new driver')
      }
    }

    if (driver) {
      try {
        await assignDriver(loadOffer.refNumber, driver)

        analytics.track(['Load Setup', 'Driver Assign Step', 'Assign Driver'])

        if (isAddingNewDriver) {
          const createdDriver = {
            ...driver,
            _type: 'driver',
          }
          // Select from Miranda has _type on Selectable type and it's needed to set value on Select
          setDriverOptions(driverOption => [...driverOption, createdDriver])
          setSelectedDriver(createdDriver)
        }

        onComplete()
      } catch {
        toast.error('Failed to assign the driver')
      }
    }
  }

  const populateDriver = useCallback(
    (shipment: Shipment) => {
      if (shipment.driver && selectedDriver === undefined) {
        setSelectedDriver({
          ...shipment.driver,
          // @ts-expect-error
          _type: 'driver',
        })

        onMarkAsComplete()
      }
    },
    [onMarkAsComplete, selectedDriver]
  )

  useEffect(
    function populateSelectedDriver() {
      populateDriver(shipment)
    },
    [shipment, populateDriver]
  )

  useEffect(() => {
    if (drivers) {
      setDriverOptions(drivers)
    }
  }, [drivers])

  function driversDatasource(): SelectDatasource<Driver> {
    return {
      type: 'driver',
      adapter: {
        getKey: driver => driver.id!,
        getLabel: driver => driver.name,
      },
      fetch({ regex }) {
        return driverOptions.filter(({ name }) => regex.test(name)).concat([ADD_NEW_DRIVER_OPTION])
      },
    }
  }

  function CustomOption({ value }: SelectOptionProps) {
    const select = useSelect()
    const option = select.getOption(value)
    const query = select.query
    const { id, name } = option as Driver
    const isAddNewOption = id === ADD_NEW_DRIVER_OPTION.id
    const leading = isAddNewOption ? <Icon name="plus" /> : undefined

    return (
      <Select.Option value={value} leading={leading}>
        <HighlightMatch text={name} match={query} />
      </Select.Option>
    )
  }

  return (
    <AsyncContent status={driversStatus} loadingState={<LoadingState />}>
      <Wrapper>
        <ImageWrapper>
          <img src={SelectDriverImage} alt="Driver Selection" />
        </ImageWrapper>
        <ContentWrapper>
          <Layout.Stack>
            <InputGroup>
              <Label htmlFor="driver-select">Tell us who will haul this load.</Label>
              <Select
                id="driver-select"
                name="driver-select"
                placeholder="Select driver"
                // @ts-expect-error TODO: Change Miranda to export Selectable
                value={selectedDriver}
                onChange={event => setSelectedDriver(event.target.value as Driver)}
                datasources={[driversDatasource]}
                components={{ Option: CustomOption }}
              />
            </InputGroup>
            {isAddingNewDriver && (
              <>
                <InputGroup>
                  <Label htmlFor="driver-name">Driver</Label>
                  <Input
                    id="driver-name"
                    name="driver-name"
                    placeholder="e.g. Jack Smith"
                    // @ts-expect-error TODO: Change Miranda to either export the enum or to accept string here
                    status={hasNameError ? 'danger' : undefined}
                    trailing={hasNameError ? <Icon name="attention" /> : undefined}
                    width="100%"
                    value={name}
                    onChange={event => {
                      setName(event.target.value)
                    }}
                  />
                  {hasNameError &&
                    createDriverErrors?.errors.name?.map(error => (
                      <ErrorMessage key={error}>{error}</ErrorMessage>
                    ))}
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="driver-phone">Driver's phone number</Label>
                  <Input
                    id="driver-phone"
                    name="driver-phone"
                    type="tel"
                    placeholder="e.g. +1 123-456-789"
                    // @ts-expect-error TODO: Change Miranda to either export the enum or to accept string here
                    status={hasPhoneNumberError ? 'danger' : undefined}
                    trailing={hasPhoneNumberError ? <Icon name="attention" /> : undefined}
                    value={phoneNumber}
                    onChange={event => {
                      setPhoneNumber(event.target.value)
                    }}
                  />
                  {hasPhoneNumberError &&
                    createDriverErrors?.errors.phone_number?.map(error => (
                      <ErrorMessage key={error}>{error}</ErrorMessage>
                    ))}
                </InputGroup>
              </>
            )}
            <Button
              variant="primary"
              onClick={handleFinish}
              disabled={
                isCreateDriverInvalid ||
                isAssignDriverInvalid ||
                isAssignDriverLoading ||
                isCreateDriverLoading
              }
            >
              Finish
            </Button>
          </Layout.Stack>
        </ContentWrapper>
      </Wrapper>
    </AsyncContent>
  )
}
